import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Ключ базиса в фильтре: адрес и название через «|». */
export const basisKey = (basis: { readonly address: string; readonly addressTitle: string }): string =>
  `${basis.address}|${basis.addressTitle}`;

export interface BasisGoodsFilter {
  /** Выбранный базис (`basisKey`) или null; значение, которого нет среди `bases`, не применяется. */
  readonly basis: string | null;
  /** Выбранная культура или null; значение, которого нет среди `goods`, не применяется. */
  readonly good: string | null;
  readonly setBasis: (value: { readonly address: string; readonly addressTitle: string } | null) => void;
  readonly setGood: (value: string | null) => void;
  readonly reset: () => void;
}

/**
 * Фильтр списков «Купить» и «Продать» по базису и культуре, записанный в адрес страницы (`address`, `addressTitle`,
 * `goods`): переживает обновление страницы и возврат «назад». Значение из адреса, которого нет в данных (базис удалён,
 * пришли из «Лучшей цены» с чужим базисом), не применяется, и список показывается целиком, а не пустым. Пока данные
 * не загружены (`bases`/`goods` равны null), значение из адреса сохраняется как есть.
 */
export function useBasisGoodsFilter(bases: readonly string[] | null, goods: readonly string[] | null): BasisGoodsFilter {
  const [params, setParams] = useSearchParams();
  const address = params.get('address');
  const addressTitle = params.get('addressTitle');
  // Название базиса может быть пустым (узел дерева без названия даёт addressTitle ''): тогда фильтр по адресу всё равно
  // применяется, а не пропадает молча.
  const rawBasis = address && addressTitle !== null ? basisKey({ address, addressTitle }) : null;
  const rawGood = params.get('goods');

  const basis = rawBasis && (bases == null || bases.includes(rawBasis)) ? rawBasis : null;
  const good = rawGood && (goods == null || goods.includes(rawGood)) ? rawGood : null;

  // Смена фильтра заменяет запись истории, а не добавляет: «назад» ведёт со страницы, а не по шагам фильтра.
  const update = useCallback(
    (patch: (next: URLSearchParams) => void) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          patch(next);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const setBasis = useCallback(
    (value: { readonly address: string; readonly addressTitle: string } | null) =>
      update((next) => {
        next.delete('address');
        next.delete('addressTitle');
        if (value) {
          next.set('address', value.address);
          next.set('addressTitle', value.addressTitle);
        }
      }),
    [update],
  );
  const setGood = useCallback(
    (value: string | null) =>
      update((next) => {
        if (value) {
          next.set('goods', value);
        } else {
          next.delete('goods');
        }
      }),
    [update],
  );
  const reset = useCallback(
    () =>
      update((next) => {
        next.delete('address');
        next.delete('addressTitle');
        next.delete('goods');
      }),
    [update],
  );

  return { basis, good, setBasis, setGood, reset };
}
